/**
 * Business plane sidebar — menu key ↔ subdomain read caps (TP-G26 · D23)
 *
 * Admin settings path matrix (Stream 58 · 66):
 * - `/system/settings/mail` — tenant admin roles (owner | operations_manager | hr_admin) or full menu
 * - `/system/settings/auth` — tenant auth policy + staff invites (same admin gate)
 * - `/system/settings/internal-entity-permissions` — same; denies system:read-only delegation
 * - `/system/cross-layer-view` — full menu only (owner | system:admin)
 * - `/conscious/brain` — full menu + internal entity only
 * - `/system/schema/create` — full menu only (owner | system:admin)
 * - `/system/schema/*` browse — system menu delegation (system:read | export:read)
 *
 * API guard parity (Stream 66 audit):
 * - InternalEntityPermissionAPI.assertTenantAdmin ↔ canAccessTenantAdminSettingsPath
 * - TenantMailSettingsAPI.assertTenantAdmin ↔ canAccessTenantAdminSettingsPath
 */

import {
  getBusinessDomainsByCategory,
  type BusinessDomain,
  type DomainCategory,
} from '@shared/types/business-domain';
import {
  deriveSubdomainReadsFromPermissions,
  subdomainMatchesAccess,
} from './site-permission-rbac';

const FULL_MENU_ACCESS_ROLES = new Set(['owner']);
const FULL_MENU_ACCESS_PERMISSIONS = new Set(['system:admin']);

/** Delegated export menu — export:read or system:read (owner via hasFullMenuAccess) */
const EXPORT_MENU_DELEGATED_PERMISSIONS = new Set(['export:read', 'system:read']);

/** Delegated system menu — system:read or export:read (symmetric cross-delegation) */
const SYSTEM_MENU_DELEGATED_PERMISSIONS = new Set(['system:read', 'export:read']);

/** Sidebar domain key → DomainCategory (platform/export are special) */
export const MENU_KEY_TO_DOMAIN_CATEGORY: Record<string, DomainCategory | 'platform' | 'export'> = {
  entity: 'entity',
  item: 'item',
  finance: 'finance',
  'finance-advanced': 'finance_advanced',
  space: 'space',
  knowledge: 'knowledge',
  appeal: 'appeal',
  calendar: 'calendar',
  /** Legacy menu key — M3 marketing → appeal consolidation */
  marketing: 'appeal',
  manufacturing: 'manufacturing',
  log: 'event',
  automation: 'event',
  export: 'export',
  'global-search': 'platform',
  workspace: 'platform',
  conscious: 'organizational',
  flow: 'event',
  system: 'platform',
};

export interface BusinessMenuAccessCaps {
  hasFullMenuAccess: boolean;
  readableSubdomains: Set<string>;
  /** Delegated JWT — export sidebar (export:read | system:read) */
  canSeeExportMenu: boolean;
  /** Delegated JWT — system sidebar (system:read | export:read) */
  canSeeSystemMenu: boolean;
  /** system:audit | system:admin delegation — audit surface */
  hasSystemAuditAccess: boolean;
  /** flow:execute (global or scoped) — assignee queue paths (L2-UI-WH-01 persona entry) */
  hasFlowAssigneeAccess: boolean;
}

export interface BusinessMenuAccessSource {
  entityType?: 'internal' | 'external' | 'user' | null;
  role?: string;
  permissions?: readonly string[];
}

/**
 * Fail-closed default for any actor this function does not explicitly grant
 * broad access to (see the `entityType !== 'internal'` branch below). No menu
 * key, subdomain read, export/system delegation, audit, or flow-assignee
 * access is implied — callers may still surface `global-search` via
 * `isBusinessMenuKeyVisible`. `workspace` is internal-only (public members
 * must not see the Business shell menu).
 */
const RESTRICTED_CAPS: BusinessMenuAccessCaps = {
  hasFullMenuAccess: false,
  readableSubdomains: new Set(),
  canSeeExportMenu: false,
  canSeeSystemMenu: false,
  hasSystemAuditAccess: false,
  hasFlowAssigneeAccess: false,
};

export function deriveBusinessMenuAccessCaps(
  source: BusinessMenuAccessSource
): BusinessMenuAccessCaps {
  const permissions = source.permissions ?? [];
  const role = source.role ?? '';
  const entityType = source.entityType ?? null;

  const fullCaps: BusinessMenuAccessCaps = {
    hasFullMenuAccess: true,
    readableSubdomains: new Set(),
    canSeeExportMenu: true,
    canSeeSystemMenu: true,
    hasSystemAuditAccess: true,
    hasFlowAssigneeAccess: true,
  };

  /**
   * Fail-closed (N1 RBAC correction): non-`internal` entityType (`external`,
   * `user`, or `null`/unresolved) previously received `fullCaps` here — an
   * unauthenticated-or-non-staff actor could reach owner-level menu caps,
   * including `hasSystemAuditAccess`/`hasFlowAssigneeAccess`, anywhere this
   * function is consulted directly (e.g. `SystemAuditGuard`). Business
   * subdomain RBAC only has a defined meaning for `internal` staff JWTs, so
   * everyone else gets the restricted default instead of a privilege grant.
   * `external` is additionally hard-blocked from the business plane entirely
   * by `canAccessBusinessPlane`/`BusinessPlaneGuard`; this restricted default
   * is defense in depth for any direct caller that bypasses the plane guard.
   */
  if (entityType !== 'internal') {
    return RESTRICTED_CAPS;
  }

  if (
    FULL_MENU_ACCESS_ROLES.has(role) ||
    permissions.some((perm) => FULL_MENU_ACCESS_PERMISSIONS.has(perm))
  ) {
    return fullCaps;
  }

  return {
    hasFullMenuAccess: false,
    readableSubdomains: new Set(deriveSubdomainReadsFromPermissions(permissions)),
    canSeeExportMenu: permissions.some((perm) => EXPORT_MENU_DELEGATED_PERMISSIONS.has(perm)),
    canSeeSystemMenu: permissions.some((perm) => SYSTEM_MENU_DELEGATED_PERMISSIONS.has(perm)),
    hasSystemAuditAccess: permissions.some(
      (perm) => perm === 'system:audit' || perm === 'system:admin'
    ),
    hasFlowAssigneeAccess: permissions.some(
      (perm) => perm === 'flow:execute' || perm.startsWith('flow:execute:')
    ),
  };
}

function categoryHasReadableSubdomain(
  category: DomainCategory,
  readableSubdomains: Set<string>
): boolean {
  const domains = getBusinessDomainsByCategory(category);
  if (!domains?.length) return false;
  return domains.some((subdomain) => subdomainMatchesAccess(subdomain, readableSubdomains));
}

export function isBusinessMenuKeyVisible(
  menuKey: string,
  caps: BusinessMenuAccessCaps,
  entityType: BusinessMenuAccessSource['entityType'] = null
): boolean {
  if (caps.hasFullMenuAccess) return true;

  if (menuKey === 'global-search') return true;

  /** Execution hub — internal business-plane users only (not public entityType=user) */
  if (menuKey === 'workspace') return entityType === 'internal';

  if (menuKey === 'export') return caps.canSeeExportMenu;
  if (menuKey === 'system') return caps.canSeeSystemMenu;

  if (menuKey === 'conscious') {
    return subdomainMatchesAccess('organizational_state', caps.readableSubdomains);
  }

  if (menuKey === 'flow' || menuKey === 'automation') {
    return (
      caps.hasFlowAssigneeAccess ||
      subdomainMatchesAccess('flow_definition', caps.readableSubdomains) ||
      subdomainMatchesAccess('stream_definition', caps.readableSubdomains)
    );
  }

  const category = MENU_KEY_TO_DOMAIN_CATEGORY[menuKey];
  if (!category || category === 'platform' || category === 'export') return false;

  return categoryHasReadableSubdomain(category, caps.readableSubdomains);
}

export function filterBusinessMenuKeys(
  menuKeys: readonly string[],
  source: BusinessMenuAccessSource
): string[] {
  const caps = deriveBusinessMenuAccessCaps(source);
  return menuKeys.filter((key) => isBusinessMenuKeyVisible(key, caps, source.entityType));
}

const ENTITY_SUBPATH_SUBDOMAINS: Record<string, string> = {
  '/entity/internal': 'internal_entity',
  '/entity/external': 'external_entity',
  '/entity/user': 'user_entity',
};

export function filterEntitySubPathsByCaps<T extends string>(
  subPaths: readonly T[],
  caps: BusinessMenuAccessCaps
): T[] {
  if (caps.hasFullMenuAccess) return [...subPaths];
  return subPaths.filter((path) => {
    const subdomain = ENTITY_SUBPATH_SUBDOMAINS[path];
    return subdomain ? subdomainMatchesAccess(subdomain, caps.readableSubdomains) : false;
  });
}

export function isConsciousBrainPath(path: string): boolean {
  return path === '/conscious/brain' || path.startsWith('/conscious/brain/');
}

export function isCrossLayerViewPath(path: string): boolean {
  return path === '/system/cross-layer-view' || path.startsWith('/system/cross-layer-view/');
}

export function isSchemaCreatePath(path: string): boolean {
  return path === '/system/schema/create' || path.startsWith('/system/schema/create/');
}

export function isSchemaBrowsePath(path: string): boolean {
  if (isSchemaCreatePath(path)) return false;
  return path === '/system/schema' || path.startsWith('/system/schema/');
}

/** E2 event plane — v2 menu paths under /event/* (flow footer SSOT) */
const EVENT_V2_PATH_PREFIXES: ReadonlyArray<{ prefix: string; subdomain: BusinessDomain }> = [
  { prefix: '/event/flow-definition', subdomain: 'flow_definition' },
  { prefix: '/event/action-binding', subdomain: 'action_binding' },
  { prefix: '/event/flow-execution', subdomain: 'flow_execution' },
  { prefix: '/event/flow-event', subdomain: 'flow_event' },
];

const EVENT_LEGACY_V1_PATH_PREFIXES = [
  '/event/stream-definition',
  '/event/stream-execution',
  '/event/stream-event',
] as const;

export function isEventPlanePath(path: string): boolean {
  return path === '/event' || path.startsWith('/event/');
}

export function isEventLegacyV1Path(path: string): boolean {
  return EVENT_LEGACY_V1_PATH_PREFIXES.some(
    (prefix) => path === prefix || path.startsWith(`${prefix}/`)
  );
}

function canAccessEventPlanePath(path: string, caps: BusinessMenuAccessCaps): boolean {
  if (caps.hasFullMenuAccess) return true;

  if (isEventLegacyV1Path(path)) {
    return false;
  }

  for (const { prefix, subdomain } of EVENT_V2_PATH_PREFIXES) {
    if (path === prefix || path.startsWith(`${prefix}/`)) {
      return subdomainMatchesAccess(subdomain, caps.readableSubdomains);
    }
  }

  return categoryHasReadableSubdomain('event', caps.readableSubdomains);
}

/** Assignee queue paths — warehouse dispatch persona (L2-UI-WH-01 · L2-UI-WH-02) */
export function isFlowAssigneeQueuePath(path: string): boolean {
  return (
    path === '/flow/dispatch' ||
    path.startsWith('/flow/dispatch/') ||
    path === '/flow/assignments' ||
    path.startsWith('/flow/assignments/') ||
    path === '/warehouse/picking-list' ||
    path.startsWith('/warehouse/picking-list/')
  );
}

/** Tenant mail + internal-entity permission matrix — admin settings, not system:read browse */
const TENANT_ADMIN_SETTINGS_ROLES = new Set(['owner', 'operations_manager', 'hr_admin']);

export function isTenantAdminSettingsPath(path: string): boolean {
  return (
    path === '/system/settings/mail' ||
    path.startsWith('/system/settings/mail/') ||
    path === '/system/settings/auth' ||
    path.startsWith('/system/settings/auth/') ||
    path === '/system/settings/internal-entity-permissions' ||
    path.startsWith('/system/settings/internal-entity-permissions/') ||
    path === '/system/settings/workbench-role-config' ||
    path.startsWith('/system/settings/workbench-role-config/')
  );
}

/**
 * Mail settings · permission delegation matrix — tenant admin roles or full menu.
 * Symmetric with TenantMailSettingsAPI / InternalEntityPermissionAPI assertTenantAdmin.
 */
export function canAccessTenantAdminSettingsPath(
  path: string,
  caps: BusinessMenuAccessCaps,
  entityType: BusinessMenuAccessSource['entityType'],
  role?: string
): boolean {
  if (!isTenantAdminSettingsPath(path)) return false;
  if (entityType !== 'internal') return false;
  if (caps.hasFullMenuAccess) return true;
  return TENANT_ADMIN_SETTINGS_ROLES.has(role ?? '');
}

/**
 * Brain control surface — internal entity + conscious admin (owner | system:admin).
 * Deep link only; not in sidebar. Other /conscious/* paths use subdomain read caps.
 */
export function canAccessConsciousBrainPath(
  path: string,
  caps: BusinessMenuAccessCaps,
  entityType: BusinessMenuAccessSource['entityType']
): boolean {
  if (!isConsciousBrainPath(path)) return false;
  if (entityType !== 'internal') return false;
  return caps.hasFullMenuAccess;
}

/**
 * 4계층 탐색 — owner | system:admin only (U8 operator surface).
 * TopBar + route guard share this SSOT (Stream 38 · 58 · U8).
 */
export function canAccessCrossLayerViewPath(path: string, caps: BusinessMenuAccessCaps): boolean {
  if (!isCrossLayerViewPath(path)) return false;
  return caps.hasFullMenuAccess;
}

/** Schema create UI — owner | system:admin only. Browse/list uses system menu delegation. */
export function canAccessSchemaCreatePath(path: string, caps: BusinessMenuAccessCaps): boolean {
  if (!isSchemaCreatePath(path)) return false;
  return caps.hasFullMenuAccess;
}

export const SYSTEM_AUDIT_PATH = '/system/audit' as const;

export function isSystemAuditPath(path: string): boolean {
  return path === SYSTEM_AUDIT_PATH || path.startsWith(`${SYSTEM_AUDIT_PATH}/`);
}

export function canAccessSystemAuditPath(path: string, caps: BusinessMenuAccessCaps): boolean {
  if (!isSystemAuditPath(path)) return false;
  return caps.hasFullMenuAccess || caps.hasSystemAuditAccess;
}

export function canAccessSchemaBrowsePath(path: string, caps: BusinessMenuAccessCaps): boolean {
  if (!isSchemaBrowsePath(path)) return false;
  return caps.hasFullMenuAccess || caps.canSeeSystemMenu;
}

/**
 * MainRouter root aliases (`/`, `/today`, `/marketing`) only Navigate — they
 * must pass BusinessPathGuard so the redirect can run. Destination paths are
 * re-checked on the subsequent navigation. `/marketing` is the legacy appeal
 * alias (M3 consolidation), not a separate authority surface.
 */
export function isBusinessPlaneRootAliasPath(path: string): boolean {
  return (
    path === '/' ||
    path === '/today' ||
    path.startsWith('/today/') ||
    path === '/marketing' ||
    path.startsWith('/marketing/')
  );
}

function isApprovalsPath(path: string): boolean {
  return path === '/approvals' || path.startsWith('/approvals/');
}

function isWarehousePath(path: string): boolean {
  return path === '/warehouse' || path.startsWith('/warehouse/');
}

function isInventoryPath(path: string): boolean {
  return path === '/inventory' || path.startsWith('/inventory/');
}

function isPersonalSettingsPath(path: string): boolean {
  return path === '/settings' || path.startsWith('/settings/');
}

function isPhysicalAccessPath(path: string): boolean {
  return path === '/physical-access' || path.startsWith('/physical-access/');
}

function hasFlowDefinitionAccess(caps: BusinessMenuAccessCaps): boolean {
  return (
    subdomainMatchesAccess('flow_definition', caps.readableSubdomains) ||
    subdomainMatchesAccess('stream_definition', caps.readableSubdomains)
  );
}

/**
 * Business-plane prefixes that are not sidebar menu keys (N1 prefix completeness).
 * Auth model mirrors where those routes actually live in derived nav:
 * - approvals / warehouse → flow footer
 * - inventory → item group
 * - settings / physical-access → workspace-adjacent always-on for admitted internals
 */
function canAccessNonMenuKeyBusinessPrefix(
  path: string,
  visibleDomainKeys: readonly string[],
  caps: BusinessMenuAccessCaps
): boolean | null {
  if (isApprovalsPath(path)) {
    if (caps.hasFullMenuAccess) return true;
    if (!visibleDomainKeys.includes('flow')) return false;
    if (caps.hasFlowAssigneeAccess && !hasFlowDefinitionAccess(caps)) {
      // Approvals are not assignee-queue paths — require definition-level flow access.
      return false;
    }
    return isBusinessMenuKeyVisible('flow', caps);
  }

  if (isWarehousePath(path)) {
    if (caps.hasFullMenuAccess) return true;
    if (isFlowAssigneeQueuePath(path) && caps.hasFlowAssigneeAccess) return true;
    if (!visibleDomainKeys.includes('flow')) return false;
    if (caps.hasFlowAssigneeAccess && !hasFlowDefinitionAccess(caps)) {
      return isFlowAssigneeQueuePath(path);
    }
    return isBusinessMenuKeyVisible('flow', caps);
  }

  if (isInventoryPath(path)) {
    if (caps.hasFullMenuAccess) return true;
    if (!visibleDomainKeys.includes('item')) return false;
    return isBusinessMenuKeyVisible('item', caps);
  }

  if (isPersonalSettingsPath(path) || isPhysicalAccessPath(path)) {
    if (caps.hasFullMenuAccess) return true;
    return visibleDomainKeys.includes('workspace');
  }

  return null;
}

export function canAccessBusinessPath(
  path: string,
  visibleDomainKeys: readonly string[],
  caps: BusinessMenuAccessCaps,
  entityType: BusinessMenuAccessSource['entityType'] = null,
  role?: string
): boolean {
  if (isBusinessPlaneRootAliasPath(path)) {
    return true;
  }

  if (canAccessConsciousBrainPath(path, caps, entityType)) return true;
  if (isConsciousBrainPath(path)) return false;

  if (canAccessCrossLayerViewPath(path, caps)) return true;
  if (isCrossLayerViewPath(path)) return false;

  if (isTenantAdminSettingsPath(path)) {
    return canAccessTenantAdminSettingsPath(path, caps, entityType, role);
  }

  if (isSchemaCreatePath(path)) {
    return canAccessSchemaCreatePath(path, caps);
  }

  if (canAccessSystemAuditPath(path, caps)) return true;
  if (isSystemAuditPath(path)) return false;

  if (isSchemaBrowsePath(path)) {
    return canAccessSchemaBrowsePath(path, caps);
  }

  if (isFlowAssigneeQueuePath(path) && caps.hasFlowAssigneeAccess) {
    return true;
  }

  if (isEventPlanePath(path)) {
    if (!caps.hasFullMenuAccess) {
      if (!visibleDomainKeys.includes('flow')) {
        return false;
      }
      if (caps.hasFlowAssigneeAccess) {
        if (!hasFlowDefinitionAccess(caps)) {
          return false;
        }
      }
    }
    return canAccessEventPlanePath(path, caps);
  }

  if (caps.hasFullMenuAccess) return true;

  /** Execution hub — internal business-plane users only */
  if (path === '/workspace' || path.startsWith('/workspace/')) {
    return entityType === 'internal';
  }

  const nonMenuKeyAccess = canAccessNonMenuKeyBusinessPrefix(path, visibleDomainKeys, caps);
  if (nonMenuKeyAccess !== null) {
    return nonMenuKeyAccess;
  }

  /** Global-search home — visible whenever global-search menu is shown */
  if (
    (path === '/system/dashboard' || path.startsWith('/system/dashboard/')) &&
    visibleDomainKeys.includes('global-search')
  ) {
    return true;
  }

  const domainKey = visibleDomainKeys.find(
    (key) => path === `/${key}` || path.startsWith(`/${key}/`)
  );
  if (!domainKey) return false;

  if (path.startsWith('/entity/')) {
    const subPaths = filterEntitySubPathsByCaps(
      ['/entity/internal', '/entity/external', '/entity/user'],
      caps
    );
    return subPaths.some((sub) => path === sub || path.startsWith(`${sub}/`));
  }

  if (domainKey === 'flow' && caps.hasFlowAssigneeAccess && !caps.hasFullMenuAccess) {
    if (!hasFlowDefinitionAccess(caps)) {
      return isFlowAssigneeQueuePath(path);
    }
  }

  return isBusinessMenuKeyVisible(domainKey, caps, entityType);
}

const MENU_KEY_DEFAULT_PATH: Record<string, string> = {
  'global-search': '/system/dashboard',
  workspace: '/workspace',
  entity: '/entity/internal',
  item: '/item',
  finance: '/finance',
  'finance-advanced': '/finance-advanced/dashboard',
  space: '/space',
  knowledge: '/knowledge',
  appeal: '/appeal',
  calendar: '/calendar',
  automation: '/automation',
  marketing: '/appeal/dashboard',
  manufacturing: '/manufacturing/dashboard',
  log: '/log',
  export: '/export',
  conscious: '/conscious',
  flow: '/flow',
  system: '/system/dashboard',
};

/** Admin-only menu paths — must align with canAccessBusinessPath (S5 parity) */
export const ADMIN_ONLY_MENU_PATHS = [
  '/conscious/brain',
  '/system/cross-layer-view',
  '/system/schema/create',
] as const;

export function isAdminOnlyMenuPath(path: string): boolean {
  return ADMIN_ONLY_MENU_PATHS.some(
    (adminPath) => path === adminPath || path.startsWith(`${adminPath}/`)
  );
}

/** First allowed business path for redirect after route guard denial */
export function resolveBusinessMenuFallbackPath(visibleDomainKeys: readonly string[]): string {
  for (const key of visibleDomainKeys) {
    if (key === 'global-search') continue;
    const path = MENU_KEY_DEFAULT_PATH[key];
    if (path) return path;
  }
  return '/system/dashboard';
}
